import { Spinner, useTranslation } from '@hbs-us/ui';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { ApiError } from '../../lib/apiFetch.js';
import { useQcJoinMutation } from './useQc.js';

/**
 * Where a scanned QR lands.
 *
 * The token is in the PATH here only because a QR has to encode something scannable; it is
 * immediately exchanged over POST and the URL is replaced, so it does not stay in history as a
 * shareable link. It was never a credential — the api requires a signed-in employee of the tenant
 * regardless — but leaving it sitting in the address bar invites somebody to paste it to a friend
 * and be surprised when that works differently than they expect.
 *
 * An unauthenticated scan hits AuthGuard first, signs in, and returns here, because the guard
 * renders the login screen in place rather than navigating away.
 */
export function QcJoinRoute() {
  const { t } = useTranslation();
  const { token } = useParams({ from: '/qc/join/$token' });
  const navigate = useNavigate();
  const join = useQcJoinMutation();
  // A scan must redeem exactly once; React strict mode would otherwise fire it twice.
  const redeemed = useRef(false);

  useEffect(() => {
    if (redeemed.current || !token) return;
    redeemed.current = true;
    join.mutate(
      { token },
      {
        onSuccess: (response) => {
          if (!response.checklistId) return;
          void navigate({
            to: '/qc/$id',
            params: { id: String(response.checklistId) },
            replace: true,
          });
        },
      },
    );
  }, [token, join, navigate]);

  if (join.isError) {
    const code = join.error instanceof ApiError ? join.error.message : 'generic';
    return (
      <section
        role="alert"
        className="flex h-full flex-col items-center justify-center gap-3 bg-background p-12 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('qc:join.failedHeading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">
          {t(`qc:errors.${code}`, { defaultValue: t('qc:join.failedBody') })}
        </p>
      </section>
    );
  }

  return (
    <div className="flex h-full items-center justify-center bg-background">
      <Spinner label={t('qc:join.joining')} />
    </div>
  );
}
